import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ToastProps } from '../../utils/hooks/useToast';
import { RootState } from '../store';

export interface Notification extends Pick<ToastProps, 'title' | 'body'> {
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    created_at: Date;
    isExecutedByStopOrder?: boolean;
    isPartOfOcoOrder?: boolean;
    account?: string;
    seen: boolean;
}

export interface NotificationState {
    notifications: Notification[];
    hideTillNextLogin: boolean;
    visited: boolean;
    opened: boolean;
}

const initialState: NotificationState = {
    notifications: [],
    hideTillNextLogin: false,
    visited: true,
    opened: false
};

export const notificationSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        setNotifications: (state, action: PayloadAction<NotificationState>) => {
            state.notifications = action.payload.notifications;
        },
        pushNotification: (state, action: PayloadAction<Notification>) => {
            state.notifications = [action.payload, ...state.notifications];
            if (state.visited) state.visited = false;
        },
        pushBatchNotifications: (state, action: PayloadAction<Notification[]>) => {
            state.notifications = [...action.payload, ...state.notifications];
            if (state.visited) state.visited = false;
        },
        setHideNotificationsTillNextLogin: (state, action: PayloadAction<boolean>) => {
            state.hideTillNextLogin = action.payload;
        },
        setNotificationsVisited: (state, action: PayloadAction<boolean>) => {
            state.visited = action.payload;
        },
        markAsSeen: (state, action: PayloadAction<string>) => {
            const notification = state.notifications.find(n => n.id === action.payload);
            if (notification) notification.seen = true;
            
        },
        markAllAsSeen: (state) => {
            state.notifications.forEach(notification => notification.seen = true);
        },
        openNotifications: (state) => {
            state.opened = true;
        },
        closeNotifications: (state) => {
            state.opened = false;
        },
        toggleNotifications: (state) => {
            state.opened = !state.opened;
        }
    }
});

export const {
    setNotifications,
    pushNotification,
    pushBatchNotifications,
    setNotificationsVisited,
    setHideNotificationsTillNextLogin,
    markAsSeen,
    markAllAsSeen,
    openNotifications,
    closeNotifications,
    toggleNotifications
} = notificationSlice.actions;

export const selectNotifications = (state: RootState) => state.notifications.notifications;
export const selectNotificationsOpened = (state: RootState) => state.notifications.opened;
export const selectNotificationsVisited = (state: RootState) => state.notifications.visited;
export const selectNotificationsHideTillNextLogin = (state: RootState) => state.notifications.hideTillNextLogin;

export default notificationSlice.reducer;
